import { type FC, useMemo } from "react";
import cn from "classnames";

import { EditPriorityLinkButton } from "../../../buttons/linkButtons";
import { ContentType, type PeopleType } from "features/Library/PeopleAssignments/types";
import { type IFlowAssignmentModelItem } from "features/People/types";
import { type FlowCardBaseProps } from "../../../cards/FlowCard/types";

import getCardPriorityIcon from "../../priority/getCardPriorityIcon";
import PublishStatusCard from "../../../cards/PublishStatusCard";
import { RouteNames } from "enums";
import RemovePriorityButton from "components/buttons/linkButtons/RemovePriorityButton/RemovePriorityButton";
import { ObjectiveLabel } from "features/Library/Flows/common/ObjectiveLabel/ObjectiveLabel";

import "./peopleFlowCard.scss";

export interface PeopleFlowCardProps extends FlowCardBaseProps<IFlowAssignmentModelItem> {
  peopleType: PeopleType;
  onRemove?: (id: number) => void;
  onEditPriority?: (id: number) => void;
  hasDeepLink?: boolean;
}

const PeopleFlowCard: FC<PeopleFlowCardProps> = (props: PeopleFlowCardProps) => {
  const { onSelect, onRemove, onEditPriority, item, selected, disabled, peopleType, disablePopupMenu, hasDeepLink } =
    props;
  const { id, published, priorityLevelId, dueDate, daysToComplete, canRemove, goals } = item;

  const flowCardMenuButtons = useMemo(() => {
    const buttons = [];
    if (onEditPriority) {
      buttons.push(<EditPriorityLinkButton key="edit-priority-menu-button" onClick={() => onEditPriority?.(id)} />);
    }
    if (onRemove) {
      buttons.push(
        <RemovePriorityButton
          onClick={() => onRemove?.(id)}
          isDisabled={!canRemove}
          peopleType={peopleType}
          context="people"
          entityType={ContentType.Flow}
          multiple={false}
          showTooltip={!canRemove}
        />,
      );
    }
    return buttons;
  }, [onEditPriority, onRemove, id, peopleType, canRemove]);

  const icons = [
    getCardPriorityIcon({
      peopleType,
      contentType: ContentType.Flow,
      priority: priorityLevelId,
      dueDate,
      daysToComplete,
      tooltipClass: "people-flow-priority-tooltip",
      showNone: false,
    }),
  ];

  return (
    <PublishStatusCard
      {...item}
      id={id}
      url={hasDeepLink ? `/${RouteNames.contentFlows}/${id}` : undefined}
      className={cn("people-flow-card")}
      onSelectionChanged={onSelect}
      popupMenuButtons={flowCardMenuButtons}
      disablePopupMenu={selected || disablePopupMenu}
      published={published}
      icons={icons}
      selected={selected}
      disabled={disabled}
      thumbnailContent={<ObjectiveLabel objective={goals?.objective} />}
      isHidden={item.hidden}
    />
  );
};

export default PeopleFlowCard;
